
import React, { useState, useEffect } from 'react'; // Adicionado useState e useEffect

interface Props {
    value: number;
    onChange: (value: number) => void;
    color: string;
    min?: number;
    max?: number;
    description?: string | React.ReactNode;
}

export const AngleSelector: React.FC<Props> = ({ value, onChange, color, min = 10, max = 180, description }) => {
    const [internalValue, setInternalValue] = useState(value);

    useEffect(() => {
        // Atualiza o estado interno se a prop externa mudar, mas apenas se não estivermos digitando
        if (value !== internalValue) {
            setInternalValue(value);
        }
    }, [value]);

    // Convert angle to SVG path coordinates
    const radius = 90;
    const centerX = 100;
    const centerY = 90;

    const calculatePath = (angleDegrees: number) => {
        const halfAngle = angleDegrees / 2;
        const startAngleRad = (halfAngle - 90) * (Math.PI / 180);
        const endAngleRad = (-halfAngle - 90) * (Math.PI / 180);

        const x1 = centerX + radius * Math.cos(startAngleRad);
        const y1 = centerY + radius * Math.sin(startAngleRad);

        const x2 = centerX + radius * Math.cos(endAngleRad);
        const y2 = centerY + radius * Math.sin(endAngleRad);

        return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 0 0 ${x2} ${y2} Z`;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInternalValue(parseFloat(val) || 0); // Atualiza o estado interno imediatamente para feedback visual
    };

    const handleBlur = () => {
        let num = parseFloat(internalValue.toString()); // Usar internalValue para o blur
        if (isNaN(num)) num = min;

        // Clamp constraints
        if (num < min) num = min;
        if (num > max) num = max;

        setInternalValue(num); // Garante que o display reflete o valor clamped
        onChange(num); // Chama o onChange do pai com o valor final
    };

    return (
        <div className="flex flex-col h-full bg-card-bg/50 dark:bg-sal-gunmetal p-4 rounded-xl border border-border-dim shadow-inner relative">
            <style>{`
            /* Custom Range Slider Styling for Better Grabbability */
            .custom-range {
                -webkit-appearance: none;
                width: 100%;
                background: transparent;
            }
            .custom-range:focus {
                outline: none;
            }
            /* Webkit (Chrome, Safari, Edge) */
            .custom-range::-webkit-slider-thumb {
                -webkit-appearance: none;
                height: 20px;
                width: 20px;
                border-radius: 50%;
                background: ${color};
                cursor: pointer;
                margin-top: -8px; /* Center thumb on track */
                box-shadow: 0 0 0 4px rgba(var(--sal-cyber-teal-rgb, 6, 182, 212), 0.1);
                transition: transform 0.1s;
            }
            .custom-range::-webkit-slider-thumb:hover {
                transform: scale(1.1);
            }
            .custom-range::-webkit-slider-runnable-track {
                width: 100%;
                height: 4px;
                cursor: pointer;
                background: rgb(var(--border-main));
                border-radius: 2px;
            }
            /* Firefox */
            .custom-range::-moz-range-thumb {
                height: 20px;
                width: 20px;
                border: none;
                border-radius: 50%;
                background: ${color};
                cursor: pointer;
                box-shadow: 0 0 0 4px rgba(var(--sal-cyber-teal-rgb, 6, 182, 212), 0.1);
            }
            .custom-range::-moz-range-track {
                width: 100%;
                height: 4px;
                cursor: pointer;
                background: rgb(var(--border-main));
                border-radius: 2px;
            }
            /* Hide Number Spinners */
            input[type=number]::-webkit-inner-spin-button, 
            input[type=number]::-webkit-outer-spin-button { 
                -webkit-appearance: none; 
                margin: 0; 
            }
        `}</style>

            <div className="flex justify-between items-start mb-3">
                <div>
                    <span className="text-sm font-bold text-text-primary uppercase tracking-wide block">Abertura da Rota (Cone)</span>
                    <span className="text-xs text-text-muted font-medium">Ângulo máximo de desvio</span>
                    {description && <div className="text-xs text-text-muted/80 font-medium leading-tight mt-1 max-w-[200px]">{description}</div>}
                </div>
                <div className="flex items-baseline border-b border-white/10 hover:border-white/30 transition-colors">
                    <input
                        type="number"
                        value={internalValue} // Usar internalValue aqui
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className="w-12 bg-transparent text-3xl font-mono font-bold text-right outline-none p-0 m-0"
                        style={{ color: color }}
                    />
                    <span className="text-xl font-bold ml-0.5" style={{ color: color }}>º</span>
                </div>
            </div>

            {/* Visualizer Area */}
            <div className="relative flex-1 bg-black/5 dark:bg-black/40 rounded-lg border border-border-dim/20 overflow-hidden mb-4 min-h-[140px]">
                {/* Grid / Radar Background */}
                <div className="absolute inset-0 opacity-20 dark:opacity-10 pointer-events-none">
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[180%] h-[180%] border border-text-primary/20 rounded-full"></div>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[120%] h-[120%] border border-text-primary/20 rounded-full"></div>
                    <div className="absolute top-0 bottom-0 left-1/2 w-px bg-text-primary/20"></div>
                </div>

                <svg viewBox="0 0 200 100" className="w-full h-full pointer-events-none relative z-10" preserveAspectRatio="xMidYMax meet">
                    {/* The Cone */}
                    <path
                        d={calculatePath(value)}
                        fill={color}
                        fillOpacity="0.3"
                        stroke={color}
                        strokeWidth="2"
                        className="drop-shadow-[0_0_10px_rgba(0,0,0,0.5)] transition-all duration-300 ease-in-out" // Ajustado para transição suave
                    />

                    {/* The Hub */}
                    <circle cx={centerX} cy={centerY} r="4" fill="#1e293b" stroke={color} strokeWidth="2" />
                </svg>
            </div>

            {/* Improved Slider Input */}
            <div className="relative pt-1 pb-1">
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={1}
                    value={value}
                    onChange={(e) => onChange(parseInt(e.target.value))}
                    className="custom-range"
                />
                <div className="flex justify-between text-xs text-text-muted font-bold mt-1 uppercase">
                    <span>{min}º (Estreito)</span>
                    <span>{max}º (Largo)</span>
                </div>
            </div>
        </div>
    );
};
